import { Minus, Plus } from "lucide-react";
import { Input } from "./ui/input";

/**
 * Reusable +/- stepper with a typeable number input in between.
 * Props:
 *   value: number
 *   onChange: (number) => void
 *   min: number (default 1)
 *   size: "md" | "sm" (default "md")
 *   testid: base data-testid; emits `${testid}-minus` and `${testid}-plus` for buttons
 */
export default function QuantityStepper({
  value,
  onChange,
  min = 1,
  size = "md",
  testid = "qty",
}) {
  const v = Number.isFinite(Number(value)) ? Number(value) : min;

  const dec = () => onChange(Math.max(min, v - 1));
  const inc = () => onChange(v + 1);

  const handleType = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange("");
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    onChange(Math.max(min, n));
  };

  const handleBlur = () => {
    if (value === "" || value === null || value === undefined) onChange(min);
  };

  const sizes =
    size === "sm"
      ? { btn: "h-9 w-9", input: "h-9 w-14 text-sm", icon: 14 }
      : { btn: "h-12 w-12", input: "h-12 w-16 text-base", icon: 16 };

  return (
    <div className="inline-flex items-stretch rounded-md border border-[#E6E2D8] bg-white overflow-hidden">
      <button
        type="button"
        onClick={dec}
        disabled={v <= min}
        data-testid={`${testid}-minus`}
        aria-label="Decrease quantity"
        className={`${sizes.btn} flex items-center justify-center text-[#5C5854] hover:bg-[#F5F2EB] hover:text-[#C84C31] disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-[#5C5854] transition-colors border-r border-[#E6E2D8]`}
      >
        <Minus size={sizes.icon} />
      </button>
      <Input
        type="number"
        inputMode="numeric"
        min={min}
        value={value}
        onChange={handleType}
        onBlur={handleBlur}
        data-testid={testid}
        className={`${sizes.input} text-center border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-white tabular-nums font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      />
      <button
        type="button"
        onClick={inc}
        data-testid={`${testid}-plus`}
        aria-label="Increase quantity"
        className={`${sizes.btn} flex items-center justify-center text-[#5C5854] hover:bg-[#F5F2EB] hover:text-[#C84C31] transition-colors border-l border-[#E6E2D8]`}
      >
        <Plus size={sizes.icon} />
      </button>
    </div>
  );
}
